/* Two-handle slider for number filters such as Alcohol % (Filters block >
   Show as a slider). The handles step through the values that actually exist,
   and on release tick the matching boxes in the hidden list, so the filter
   itself is still Shopify's own. Moving both handles to the ends clears it. */
(() => {
  if (customElements.get('pbw-value-slider')) return;

  const fmt = (n) => String(Math.round(n * 10) / 10);

  class PbwValueSlider extends HTMLElement {
    connectedCallback() {
      this.setup();
      if (!this.wired) {
        this.wired = true;
        this.addEventListener('input', this.onInput);
        // The on:change attribute on this element keeps Horizon's own change
        // handler from firing for the handles (it would refresh before the
        // boxes are ticked). This ticks them, then asks Horizon to refresh.
        this.addEventListener('change', this.onChange);
        // Horizon refreshes the filters in place after each choice, which puts
        // the handles back to their blank state. Read the ticked boxes again.
        new MutationObserver(() => {
          if (this.queued) return;
          this.queued = true;
          queueMicrotask(() => {
            this.queued = false;
            this.setup();
          });
        }).observe(this, { subtree: true, attributes: true, attributeFilter: ['checked', 'min', 'max', 'data-n'] });
      }
    }

    setup() {
      this.lo = this.querySelector('[data-pbw-lo]');
      this.hi = this.querySelector('[data-pbw-hi]');
      this.label = this.querySelector('.pbw-ps__label');
      this.fill = this.querySelector('.pbw-ps__fill');
      this.boxes = [...this.querySelectorAll('input[type="checkbox"]')];
      if (!this.lo || !this.hi) return;

      this.nums = [...new Set(this.boxes.map((b) => parseFloat(b.dataset.n)).filter((n) => !Number.isNaN(n)))].sort(
        (a, b) => a - b
      );
      if (this.nums.length < 2) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const last = this.nums.length - 1;
      for (const r of [this.lo, this.hi]) {
        if (r.getAttribute('min') !== '0') r.setAttribute('min', '0');
        if (r.getAttribute('max') !== String(last)) r.setAttribute('max', String(last));
        if (r.getAttribute('step') !== '1') r.setAttribute('step', '1');
      }
      const active = this.boxes.filter((b) => b.checked).map((b) => parseFloat(b.dataset.n));
      this.lo.value = String(active.length ? this.nums.indexOf(Math.min(...active)) : 0);
      this.hi.value = String(active.length ? this.nums.indexOf(Math.max(...active)) : last);
      this.paint();
    }

    onInput = (event) => {
      if (event.target === this.lo || event.target === this.hi) event.stopPropagation();
      const a = Number(this.lo.value);
      const b = Number(this.hi.value);
      if (a > b) {
        if (event.target === this.lo) this.lo.value = String(b);
        else this.hi.value = String(a);
      }
      this.paint();
    };

    onChange = (event) => {
      if (event.target !== this.lo && event.target !== this.hi) return;
      const i = Number(this.lo.value);
      const j = Number(this.hi.value);
      const all = i === 0 && j === this.nums.length - 1;
      const a = this.nums[i];
      const b = this.nums[j];
      for (const box of this.boxes) {
        const n = parseFloat(box.dataset.n);
        box.checked = !all && n >= a && n <= b;
      }
      const group = this.closest('facet-inputs-component');
      if (group && typeof group.updateFilters === 'function') group.updateFilters();
    };

    paint() {
      if (!this.nums || this.nums.length < 2) return;
      const last = this.nums.length - 1;
      const i = Number(this.lo.value);
      const j = Number(this.hi.value);
      if (this.fill) {
        this.fill.style.left = `${(i / last) * 100}%`;
        this.fill.style.right = `${100 - (j / last) * 100}%`;
      }
      this.lo.style.zIndex = i >= last ? '2' : '';
      const unit = this.dataset.unit || '';
      const to = this.dataset.to || 'to';
      const text = `${fmt(this.nums[i])}${unit} ${to} ${fmt(this.nums[j])}${unit}`;
      if (this.label && this.label.textContent !== text) this.label.textContent = text;
      this.lo.setAttribute('aria-valuetext', `${fmt(this.nums[i])}${unit}`);
      this.hi.setAttribute('aria-valuetext', `${fmt(this.nums[j])}${unit}`);
    }
  }

  customElements.define('pbw-value-slider', PbwValueSlider);
})();
